import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { xdrChainQuery, type XdrProcess, type XdrSource, type XdrSweepKind } from '../api/securitee'
import { Spinner } from '../ui/Spinner'
import { XdrSweep } from './XdrSweep'
import './XdrChain.css'

type Sweep = (kind: XdrSweepKind, value: string) => void

/**
 * Lantul proceselor unei detectii (docs XDR 03, decizia 8), deasupra
 * cronologiei: de la radacina pana la procesul evenimentului (marcat), apoi
 * ce a pornit el, si spre ce IP-uri a vorbit fiecare (LAN marcat). Un proces
 * nesemnat e marcat; "Parent unknown" cand urcarea
 * s-a oprit fara o potrivire sigura - lantul nu ghiceste. Din hash-ul unui
 * proces si din IP-ul unei conexiuni: "Unde mai e?" (decizia 9). Din fila XDR a
 * tichetului sau din panoul incidentului (`from`).
 */
export function XdrChain({ from, detection }: { from: XdrSource; detection: number }) {
  const query = useQuery(xdrChainQuery(from, detection))
  const [sweep, setSweep] = useState<{ kind: XdrSweepKind; value: string } | null>(null)
  const onSweep: Sweep = (kind, value) => setSweep({ kind, value })

  if (query.isPending) return <Spinner />

  if (query.isError) {
    return <p className="color-red">{query.error instanceof Error ? query.error.message : 'The process chain could not be read.'}</p>
  }

  const { how, chain, parent_unknown: parentUnknown, children } = query.data

  return (
    <section className="xdr-chain" aria-label="Process chain">
      <h5 className="xdr-timeline-head">Process chain</h5>
      {chain.length === 0 ? (
        <p className="font-grey-cascade">{how === null ? 'No process is linked to this detection.' : 'The agent did not see this process.'}</p>
      ) : (
        <>
          {parentUnknown && <p className="font-grey-cascade xdr-chain-unknown">Parent unknown: no safe match further up.</p>}
          <ol className="list-unstyled xdr-chain-list">
            {chain.map((p, i) => (
              <Process key={`${p.pid}-${p.started_at}`} process={p} current={i === chain.length - 1} onSweep={onSweep} />
            ))}
          </ol>
          {children.length > 0 && (
            <div className="xdr-chain-children">
              <span className="xdr-chain-children-title">Started by it</span>
              <ul className="list-unstyled">
                {children.map((p) => (
                  <Process key={`${p.pid}-${p.started_at}`} process={p} current={false} onSweep={onSweep} />
                ))}
              </ul>
            </div>
          )}
        </>
      )}
      {sweep && <XdrSweep key={`${sweep.kind}:${sweep.value}`} from={from} kind={sweep.kind} value={sweep.value} onClose={() => setSweep(null)} />}
    </section>
  )
}

function Process({ process: p, current, onSweep }: { process: XdrProcess; current: boolean; onSweep: Sweep }) {
  return (
    <li className={`xdr-chain-process${current ? ' xdr-chain-current' : ''}`}>
      <div className="xdr-chain-line">
        <strong className="xdr-chain-name">{p.name}</strong>
        <span className="font-grey-cascade">pid {p.pid}</span>
        {p.signed === false && <span className="label label-warning">Unsigned</span>}
        {p.from_event && (
          <span
            className="label label-info xdr-chain-from-event"
            title="This process ended before the agent's process snapshot; its details come from the Windows process-start event."
          >
            Only in the Windows event
          </span>
        )}
        {p.user && <span className="font-grey-cascade">{p.user}</span>}
        <span className="font-grey-cascade">{p.started_at.slice(11, 19)} UTC</span>
      </div>
      {p.cmdline !== '' && <code className="xdr-value xdr-chain-cmd">{p.cmdline}</code>}
      {(p.connections ?? []).length > 0 && (
        <ul className="list-unstyled xdr-chain-connections" aria-label={`Connections of ${p.name}`}>
          {(p.connections ?? []).map((c) => (
            <li key={`${c.proto}-${c.remote_ip}-${c.remote_port}-${c.first_seen}`} className="xdr-chain-connection">
              <i className="fa fa-long-arrow-alt-right" aria-hidden="true"></i> {c.remote_ip}:{c.remote_port} <span className="font-grey-cascade">{c.proto}</span>
              {c.sockets > 1 ? (
                <span className="font-grey-cascade" title={`${c.sockets} connections to this address`}>
                  {' '}
                  ×{c.sockets}
                </span>
              ) : null}
              {c.private ? <span className="label label-default">LAN</span> : null}
              <span className="font-grey-cascade"> {c.first_seen.slice(11, 19)} UTC</span>{' '}
              <button type="button" className="btn btn-link btn-xs xdr-sweep-ip" onClick={() => onSweep('ip', c.remote_ip)}>
                Where else?
              </button>
            </li>
          ))}
        </ul>
      )}
      {p.sha256 !== '' && (
        <span className="font-grey-cascade xdr-chain-sha" title={p.sha256}>
          sha256 {p.sha256.slice(0, 16)}…{' '}
          <button type="button" className="btn btn-link btn-xs xdr-sweep-hash" onClick={() => onSweep('sha256', p.sha256)}>
            Where else?
          </button>
        </span>
      )}
      {p.path !== '' && (
        <button type="button" className="btn btn-link btn-xs xdr-sweep-path" onClick={() => onSweep('path', p.path)} title={p.path}>
          Same path elsewhere?
        </button>
      )}
    </li>
  )
}
